import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error en GestionPrecios:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Algo salió mal</h2>
          <p className="text-gray-600 mb-4">Ha ocurrido un error al cargar los precios.</p>
          <Button onClick={() => window.location.reload()}>
            Recargar Página
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

const GestionPrecios = () => {
  const { user, API } = useContext(AuthContext);
  const [precios, setPrecios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [nuevosPrecios, setNuevosPrecios] = useState({});
  const [filtroEstado, setFiltroEstado] = useState('activos'); // Por defecto mostrar solo activos

  const [semestres, setSemestres] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditSemesterDialogOpen, setIsEditSemesterDialogOpen] = useState(false);
  const [editingSemester, setEditingSemester] = useState(null);
  const [createForm, setCreateForm] = useState({
    nombre: '',
    descripcion: '',
    año_lectivo: new Date().getFullYear(), // Default to current year
    precio_uyu: 0,
    precio_usd: 0,
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [editSemesterForm, setEditSemesterForm] = useState({});

  useEffect(() => {
    if (user?.rol === 'admin') {
      cargarPrecios();
    }
  }, [user]);

  const cargarPrecios = async () => {
    try {
      console.log('🔄 Cargando precios...');
      // Generar ID único para evitar TODO tipo de caché
      const randomId = Math.random().toString(36).substring(7);
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/semestres/precios?_nocache=${randomId}&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log('✅ Respuesta precios recibida');
      console.log('📊 Total semestres en respuesta:', response.data.semestres?.length);
      
      const semestresData = response.data.semestres || [];
      
      // Eliminar duplicados por ID (por si acaso)
      const semestresUnicos = Array.from(
        new Map(semestresData.map(s => [s.id, s])).values()
      );
      
      console.log('Total semestres recibidos:', semestresData.length);
      console.log('Semestres únicos (sin duplicados):', semestresUnicos.length);
      
      // Debug: Mostrar años de cada semestre
      semestresUnicos.forEach(s => {
        console.log(`📅 ${s.nombre}: año_lectivo = ${s.año_lectivo}`);
      });
      
      setPrecios(semestresUnicos);
      setSemestres(semestresUnicos);
      
      console.log('Precios cargados exitosamente');
    } catch (error) {
      console.error('Error loading prices:', error);
      toast.error('Error al cargar los precios');
      // No throw el error para evitar pantalla en blanco
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSemester = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/precios-semestres`, createForm);
      toast.success('Paquete de pago creado correctamente');
      cargarPrecios();
      setIsCreateDialogOpen(false);
      setCreateForm({
        nombre: '',
        descripcion: '',
        año_lectivo: new Date().getFullYear(),
        precio_uyu: 0,
        precio_usd: 0,
        fecha_inicio: '',
        fecha_fin: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear semestre');
    }
  };

  const openEditSemesterDialog = (semestre) => {
    console.log('🔍 Abriendo modal de edición para:', semestre.nombre);
    console.log('📅 Año lectivo del objeto semestre:', semestre.año_lectivo);
    console.log('📦 Objeto completo:', semestre);
    
    setEditingSemester(semestre);
    setEditSemesterForm({
      nombre: semestre.nombre,
      descripcion: semestre.descripcion,
      año_lectivo: semestre.año_lectivo || new Date().getFullYear(),
      precio_uyu: semestre.precio_uyu,
      precio_usd: semestre.precio_usd,
      fecha_inicio: semestre.fecha_inicio?.split('T')[0] || '',
      fecha_fin: semestre.fecha_fin?.split('T')[0] || '',
      activo: semestre.activo
    });
    
    console.log('✏️ Formulario de edición configurado con año:', semestre.año_lectivo || new Date().getFullYear());
    setIsEditSemesterDialogOpen(true);
  };

  const handleEditSemester = async (e) => {
    e.preventDefault();
    
    // Guardar el estado actual por si necesitamos revertir
    const backupPrecios = [...precios];
    
    try {
      console.log('🔄 Actualizando semestre:', editingSemester.id);
      console.log('📝 Datos a enviar:', editSemesterForm);
      console.log('📅 Año lectivo a actualizar:', editSemesterForm.año_lectivo);
      console.log('📍 URL del API:', `${API}/semestres/${editingSemester.id}`);
      
      const response = await axios.put(`${API}/semestres/${editingSemester.id}`, editSemesterForm, {
        timeout: 10000, // 10 segundos timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Respuesta actualización:', response.data);
      
      toast.success('Semestre actualizado correctamente');
      
      // Recargar datos PRIMERO antes de cerrar modal
      try {
        // Forzar limpieza del estado antes de recargar
        setPrecios([]);
        setSemestres([]);
        
        // Esperar un tick para que React procese el cambio
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Ahora recargar datos frescos
        await cargarPrecios();
        console.log('✅ Datos recargados exitosamente');
      } catch (reloadError) {
        console.error('⚠️ Error recargando datos, pero actualización fue exitosa:', reloadError);
        toast.warning('Actualizado correctamente. Recarga la página para ver los cambios.');
      }
      
      // Cerrar modal DESPUÉS de recargar
      setIsEditSemesterDialogOpen(false);
      setEditingSemester(null);
      
    } catch (error) {
      console.error('❌ Error actualizando semestre:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      // Revertir al estado anterior
      setPrecios(backupPrecios);
      setSemestres(backupPrecios);
      
      // Formatear el mensaje de error correctamente
      let errorMsg = 'Error al actualizar semestre';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Si es un array de errores de validación de Pydantic
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'campo';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } 
        // Si es un string
        else if (typeof detail === 'string') {
          errorMsg = detail;
        }
        // Si es un objeto
        else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      
      // NO cerrar el modal si hay error, para que el usuario pueda reintentar
    }
  };

  const handleDeleteSemester = async (semestreId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este semestre?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/semestres/${semestreId}`);
      toast.success('Semestre eliminado correctamente');
      cargarPrecios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar semestre');
    }
  };

  const obtenerPrecioPorSemestre = (semestreId) => {
    // First try to find by semestre number, then by id
    const precio = precios.find(p => 
      p.semestre?.toString() === semestreId || 
      p.id === semestreId ||
      p.semestre === parseInt(semestreId)
    );
    return precio || { semestre: parseInt(semestreId), precio_uyu: 0, precio_usd: 0 };
  };

  const iniciarEdicion = (semestreId) => {
    const precio = obtenerPrecioPorSemestre(semestreId);
    setEditando(semestreId);
    setNuevosPrecios({
      ...nuevosPrecios,
      [semestreId]: {
        precio_uyu: precio.precio_uyu,
        precio_usd: precio.precio_usd
      }
    });
  };

  const cancelarEdicion = () => {
    setEditando(null);
    setNuevosPrecios({});
  };

  const actualizarPrecio = async (semestreId) => {
    try {
      const preciosActualizados = nuevosPrecios[semestreId];
      
      if (!preciosActualizados || preciosActualizados.precio_uyu < 0 || preciosActualizados.precio_usd < 0) {
        toast.error('Los precios deben ser números positivos');
        return;
      }

      await axios.put(`${API}/semestres/${semestreId}/precio`, {
        precio_uyu: Number(preciosActualizados.precio_uyu),
        precio_usd: Number(preciosActualizados.precio_usd)
      });

      toast.success('Precio actualizado correctamente');
      cargarPrecios();
      setEditando(null);
      setNuevosPrecios({});
    } catch (error) {
      toast.error('Error al actualizar el precio');
      console.error('Error updating price:', error);
    }
  };

  const manejarCambioPrecio = (semestreId, campo, valor) => {
    setNuevosPrecios({
      ...nuevosPrecios,
      [semestreId]: {
        ...nuevosPrecios[semestreId],
        [campo]: valor
      }
    });
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="space-y-6">
        <Card className="shadow-md border-l-4 border-red-500">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Acceso Denegado</h3>
                <p className="text-gray-600">Solo los administradores pueden gestionar los precios de semestres.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Semestres</h1>
          <p className="text-gray-600">Administra semestres, precios en pesos uruguayos y dólares</p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="instituto-gradient text-white btn-hover"
        >
          <i className="fas fa-plus mr-2"></i>
          Crear Semestre
        </Button>
      </div>

      {/* Filtro de Estado */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Mostrar:</span>
            <div className="flex gap-2">
              <Button
                variant={filtroEstado === 'todos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado('todos')}
                className={filtroEstado === 'todos' ? 'bg-blue-600' : ''}
              >
                <i className="fas fa-list mr-2"></i>
                Todos ({precios.filter(s => s && s.id).length})
              </Button>
              <Button
                variant={filtroEstado === 'activos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado('activos')}
                className={filtroEstado === 'activos' ? 'bg-green-600' : ''}
              >
                <i className="fas fa-check-circle mr-2"></i>
                Activos ({precios.filter(s => s && s.id && s.activo !== false).length})
              </Button>
              <Button
                variant={filtroEstado === 'inactivos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado('inactivos')}
                className={filtroEstado === 'inactivos' ? 'bg-red-600' : ''}
              >
                <i className="fas fa-times-circle mr-2"></i>
                Inactivos ({precios.filter(s => s && s.id && s.activo === false).length})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Semestres */}
      <div className="grid gap-6">
        {precios
          .filter(s => s && s.id)
          .filter(s => {
            if (filtroEstado === 'activos') return s.activo !== false;
            if (filtroEstado === 'inactivos') return s.activo === false;
            return true; // 'todos'
          })
          .map((semestre) => {
          const precioActual = semestre;
          const estaEditando = editando === (semestre.semestre?.toString() || semestre.id);
          
          return (
            <Card key={semestre.id || semestre.semestre} className="shadow-lg border-0">
              <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <i className="fas fa-calendar-alt text-blue-600 mr-3"></i>
                    <div>
                      <div>{semestre.nombre}</div>
                      {semestre.año_lectivo && (
                        <div className="text-sm text-blue-600 font-semibold mt-1">
                          Año Lectivo: {semestre.año_lectivo}
                        </div>
                      )}
                      {semestre.descripcion && (
                        <div className="text-sm text-gray-600 font-normal mt-1">
                          {semestre.descripcion}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge 
                      variant="secondary" 
                      className={`${semestre.activo !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                    >
                      {semestre.activo !== false ? 'Activo' : 'Inactivo'}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditSemesterDialog(semestre)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <i className="fas fa-cog mr-1"></i>
                      Config
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6">
                {!estaEditando ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <i className="fas fa-dollar-sign text-green-600 mr-2"></i>
                            <span className="font-semibold text-gray-900">Precio USD</span>
                          </div>
                          <span className="text-2xl font-bold text-green-700">
                            ${precioActual.precio_usd || 0}
                          </span>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <i className="fas fa-peso-sign text-blue-600 mr-2"></i>
                            <span className="font-semibold text-gray-900">Precio UYU</span>
                          </div>
                          <span className="text-2xl font-bold text-blue-700">
                            ${precioActual.precio_uyu || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => iniciarEdicion(semestre.semestre?.toString() || semestre.id)}
                        className="instituto-gradient text-white btn-hover flex-1"
                      >
                        <i className="fas fa-edit mr-2"></i>
                        Editar Precios
                      </Button>
                      {semestre.id && (
                        <Button
                          onClick={() => handleDeleteSemester(semestre.id)}
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio USD
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={nuevosPrecios[semestre.id]?.precio_usd || ''}
                          onChange={(e) => manejarCambioPrecio(semestre.id, 'precio_usd', e.target.value)}
                          placeholder="0.00"
                          className="focus:ring-2 focus:ring-blue-600"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio UYU
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={nuevosPrecios[semestre.id]?.precio_uyu || ''}
                          onChange={(e) => manejarCambioPrecio(semestre.id, 'precio_uyu', e.target.value)}
                          placeholder="0.00"
                          className="focus:ring-2 focus:ring-blue-600"
                        />
                      </div>
                    </div>
                    
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => actualizarPrecio(semestre.id)}
                        className="instituto-gradient text-white btn-hover flex-1"
                      >
                        <i className="fas fa-save mr-2"></i>
                        Guardar Cambios
                      </Button>
                      <Button
                        onClick={cancelarEdicion}
                        variant="outline"
                        className="flex-1"
                      >
                        <i className="fas fa-times mr-2"></i>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Información */}
      <Card className="shadow-md border-0">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <i className="fas fa-info-circle text-blue-600 mt-1"></i>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Información Importante</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Los precios se aplican inmediatamente al ser guardados</li>
                <li>• Los estudiantes podrán ver los nuevos precios en el sistema de registro</li>
                <li>• Los precios deben ser números positivos</li>
                <li>• Use punto (.) para decimales</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Semester Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Semestre</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSemester} className="space-y-4">
            <div>
              <Label htmlFor="create-nombre">Nombre del Semestre</Label>
              <Input
                id="create-nombre"
                value={createForm.nombre}
                onChange={(e) => setCreateForm({ ...createForm, nombre: e.target.value })}
                placeholder="Ej: Quinto Semestre 2024"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="create-descripcion">Descripción</Label>
              <Input
                id="create-descripcion"
                value={createForm.descripcion}
                onChange={(e) => setCreateForm({ ...createForm, descripcion: e.target.value })}
                placeholder="Descripción del semestre"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="create-año-lectivo">Año Lectivo *</Label>
              <Input
                id="create-año-lectivo"
                type="number"
                min="2020"
                max="2100"
                value={createForm.año_lectivo}
                onChange={(e) => setCreateForm({ ...createForm, año_lectivo: parseInt(e.target.value) || new Date().getFullYear() })}
                placeholder={`Ej: ${new Date().getFullYear()}`}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Año académico para este semestre (ej: 2024, 2025)
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="create-precio-usd">Precio USD</Label>
                <Input
                  id="create-precio-usd"
                  type="number"
                  min="0"
                  step="0.01"
                  value={createForm.precio_usd}
                  onChange={(e) => setCreateForm({ ...createForm, precio_usd: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="create-precio-uyu">Precio UYU</Label>
                <Input
                  id="create-precio-uyu"
                  type="number"
                  min="0"
                  step="0.01"
                  value={createForm.precio_uyu}
                  onChange={(e) => setCreateForm({ ...createForm, precio_uyu: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="create-fecha-inicio">Fecha de Inicio</Label>
                <Input
                  id="create-fecha-inicio"
                  type="date"
                  value={createForm.fecha_inicio}
                  onChange={(e) => setCreateForm({ ...createForm, fecha_inicio: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="create-fecha-fin">Fecha de Fin</Label>
                <Input
                  id="create-fecha-fin"
                  type="date"
                  value={createForm.fecha_fin}
                  onChange={(e) => setCreateForm({ ...createForm, fecha_fin: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" className="instituto-gradient text-white">
                Crear Semestre
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Semester Dialog */}
      <Dialog open={isEditSemesterDialogOpen} onOpenChange={setIsEditSemesterDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Configurar Semestre</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSemester} className="space-y-4">
            <div>
              <Label htmlFor="edit-nombre">Nombre del Semestre</Label>
              <Input
                id="edit-nombre"
                value={editSemesterForm.nombre || ''}
                onChange={(e) => setEditSemesterForm({ ...editSemesterForm, nombre: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-descripcion">Descripción</Label>
              <Input
                id="edit-descripcion"
                value={editSemesterForm.descripcion || ''}
                onChange={(e) => setEditSemesterForm({ ...editSemesterForm, descripcion: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-año-lectivo">Año Lectivo *</Label>
              <Input
                id="edit-año-lectivo"
                type="number"
                min="2020"
                max="2100"
                value={editSemesterForm.año_lectivo || new Date().getFullYear()}
                onChange={(e) => setEditSemesterForm({ ...editSemesterForm, año_lectivo: parseInt(e.target.value) || new Date().getFullYear() })}
                placeholder={`Ej: ${new Date().getFullYear()}`}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Año académico para este semestre (ej: 2024, 2025)
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-precio-usd">Precio USD</Label>
                <Input
                  id="edit-precio-usd"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editSemesterForm.precio_usd || 0}
                  onChange={(e) => setEditSemesterForm({ ...editSemesterForm, precio_usd: parseFloat(e.target.value) || 0 })}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-precio-uyu">Precio UYU</Label>
                <Input
                  id="edit-precio-uyu"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editSemesterForm.precio_uyu || 0}
                  onChange={(e) => setEditSemesterForm({ ...editSemesterForm, precio_uyu: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-fecha-inicio">Fecha de Inicio</Label>
                <Input
                  id="edit-fecha-inicio"
                  type="date"
                  value={editSemesterForm.fecha_inicio || ''}
                  onChange={(e) => setEditSemesterForm({ ...editSemesterForm, fecha_inicio: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-fecha-fin">Fecha de Fin</Label>
                <Input
                  id="edit-fecha-fin"
                  type="date"
                  value={editSemesterForm.fecha_fin || ''}
                  onChange={(e) => setEditSemesterForm({ ...editSemesterForm, fecha_fin: e.target.value })}
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-activo"
                checked={editSemesterForm.activo !== false}
                onCheckedChange={(checked) => setEditSemesterForm({ ...editSemesterForm, activo: checked })}
              />
              <Label htmlFor="edit-activo">Semestre Activo</Label>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditSemesterDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" className="instituto-gradient text-white">
                Actualizar Semestre
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wrap with ErrorBoundary
const GestionPreciosConErrorBoundary = () => (
  <ErrorBoundary>
    <GestionPrecios />
  </ErrorBoundary>
);

export default GestionPreciosConErrorBoundary;